import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaEdit, FaTrashAlt, FaChevronDown, FaSlidersH, FaEnvelope, FaPhone } from "react-icons/fa";
import "../assets/css/Users.css";
import "../assets/css/DigixLeads.css";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import useCompactViewport from "../hooks/useCompactViewport";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { SupportSortableHeader } from "./SupportSortableHeader";
import { getAuthItem } from "../utils/authStorage";
import { formatPhoneSpaced } from "../utils/phone";

const USERS_ROWS_PER_PAGE_KEY = "usersRowsPerPage";

// Employee forms don't capture a country code yet and phones are validated as Indian mobiles.
const DEFAULT_PHONE_COUNTRY_CODE = "+91";

function phoneCountryCode(user) {
  const raw = String(user.countryCode || user.country_code || user.Country_Code || "").replace(/[^\d]/g, "");
  return raw ? `+${raw}` : DEFAULT_PHONE_COUNTRY_CODE;
}

function Users({ users = [], onAddUserClick, onViewUser, onEditUser, onDeleteUser, loading = false }) {
  // Column widths (px) for resizable headers. Must match the number of <th> columns in the table.
  const EMPLOYEE_COLUMN_WIDTHS = [
    110, 200, 220, 190, 180, 230, 170, 170, 180, 170, 100, 120, 190,
    140, 140, 130, 140, 130, 200,
  ];

  const renderPhoneAction = (user) => {
    const phone = String(user.phoneNumber || user.phone_number || "").trim();
    if (!phone) {
      return (
        <span
          className="btn-mail btn-mail--call btn-mail--disabled"
          title="Phone unavailable"
          aria-label="Phone unavailable"
          aria-disabled="true"
        >
          <FaPhone style={{ transform: "scaleX(-1)" }} />
        </span>
      );
    }
    const countryCode = phone.startsWith("+") ? "" : phoneCountryCode(user);
    const label = formatPhoneSpaced(countryCode, phone);
    return (
      <a
        className="btn-mail btn-mail--call"
        href={`tel:${`${countryCode}${phone}`.replace(/[^\d+]/g, "")}`}
        title={`Call ${label}`}
        aria-label={`Call ${label}`}
        onClick={(e) => e.stopPropagation()}
      >
        <FaPhone style={{ transform: "scaleX(-1)" }} />
      </a>
    );
  };

  const renderCompanyEmailAction = (user, { tooltip = false } = {}) => {
    const companyEmail = String(user.companyEmail || user.company_email || "").trim();
    if (!companyEmail) {
      return (
        <span
          className="btn-mail btn-mail--disabled"
          title="Company email unavailable"
          aria-label="Company email unavailable"
          aria-disabled="true"
        >
          <FaEnvelope />
        </span>
      );
    }
    return (
      <a
        className={`btn-mail${tooltip ? " users-tooltip" : ""}`}
        href={`mailto:${companyEmail}`}
        title={tooltip ? undefined : `Company email: ${companyEmail}`}
        data-tooltip={tooltip ? companyEmail : undefined}
        aria-label={`Email ${companyEmail}`}
        onClick={(e) => e.stopPropagation()}
      >
        <FaEnvelope />
      </a>
    );
  };

  const { getColumnStyle, handleResizeStart } = useResizableColumns(EMPLOYEE_COLUMN_WIDTHS);

  const role = (() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  })();
  const canAdd = canPerformActionOnPage(PAGES.USERS, ACTIONS.ADD, role);
  const canEdit = canPerformActionOnPage(PAGES.USERS, ACTIONS.EDIT, role);
  const canDelete = canPerformActionOnPage(PAGES.USERS, ACTIONS.DELETE, role);
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = localStorage.getItem('usersCurrentPage');
    if (saved) {
      try {
        const parsed = parseInt(saved, 10);
        return parsed > 0 ? parsed : 1;
      } catch (e) {
        return 1;
      }
    }
    return 1;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(USERS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_ROWS_PER_PAGE;
  });

  // Filters
  const [nameFilter, setNameFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [freeSearch, setFreeSearch] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Searchable dropdown states for Role/Designation/Status filters
  const [designationDropdownOpen, setDesignationDropdownOpen] = useState(false);
  const [designationSearchQuery, setDesignationSearchQuery] = useState("");
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSearchQuery, setStatusSearchQuery] = useState("");

  const designationDropdownRef = useRef(null);
  const roleDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const isCompactViewport = useCompactViewport();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const normalizeStr = (v) =>
    v == null ? "" : String(v).toLowerCase().trim();

  const getUserDesignation = (u) => u?.designation || u?.designation_name || "";
  const getUserStatus = (u) => u?.status || "";
  const getUserName = (u) => {
    const fn = u?.firstName || u?.first_name || "";
    const ln = u?.lastName || u?.last_name || "";
    return `${fn} ${ln}`.trim();
  };
  const getUserRolesList = (u) => {
    if (Array.isArray(u?.roles) && u.roles.length > 0) {
      return u.roles
        .map((r) => r?.roleName || r?.role_name || r?.name || r?.role || "")
        .map((x) => String(x).trim())
        .filter(Boolean);
    }
    const single = u?.role || u?.role_name || "";
    return single ? [String(single).trim()] : [];
  };

  const designationOptions = useMemo(() => {
    const set = new Set();
    (users || []).forEach((u) => {
      const d = getUserDesignation(u);
      if (d && normalizeStr(d) !== "—") set.add(String(d));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const roleOptions = useMemo(() => {
    const set = new Set();
    (users || []).forEach((u) => {
      getUserRolesList(u).forEach((r) => {
        if (r && normalizeStr(r) !== "—") set.add(String(r));
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    (users || []).forEach((u) => {
      const s = getUserStatus(u);
      if (s && normalizeStr(s) !== "—") set.add(String(s));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const filteredUsers = useMemo(() => {
    const nf = normalizeStr(nameFilter);
    const df = normalizeStr(designationFilter);
    const rf = normalizeStr(roleFilter);
    const sf = normalizeStr(statusFilter);

    return (users || []).filter((u) => {
      const fullName = normalizeStr(getUserName(u));
      const designation = normalizeStr(getUserDesignation(u));
      const status = normalizeStr(getUserStatus(u));
      const rolesList = getUserRolesList(u).map(normalizeStr);

      const matchesName = !nf || fullName.includes(nf);
      const matchesDesignation = !df || designation === df;
      const matchesRole = !rf || rolesList.includes(rf);
      const matchesStatus = !sf || status === sf;

      return matchesName && matchesDesignation && matchesRole && matchesStatus;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, nameFilter, designationFilter, roleFilter, statusFilter]);

  const freeSearchFilteredUsers = useMemo(() => {
    const q = normalizeStr(freeSearch);
    if (!q) return filteredUsers;
    return (filteredUsers || []).filter((u) => {
      const roleText = getUserRolesList(u).join(", ");
      const bag = [
        getUserName(u),
        u?.email,
        u?.companyEmail ?? u?.company_email,
        getUserDesignation(u),
        roleText,
        u?.employeeId ?? u?.employee_id,
        u?.aadhaar,
        u?.pan,
        u?.department,
        u?.phoneNumber ?? u?.phone_number,
        u?.band,
        getUserStatus(u),
        u?.dateOfJoining ?? u?.date_of_joining ?? u?.doj,
        u?.dateOfLeaving ?? u?.date_of_leaving,
        u?.createdByName ?? u?.created_by_name,
        u?.createdOn ?? u?.created_on,
        u?.updatedByName ?? u?.updated_by_name,
        u?.updatedOn ?? u?.updated_on,
      ]
        .map((v) => normalizeStr(v))
        .filter(Boolean);
      return bag.some((v) => v.includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUsers, freeSearch]);

  const getEmployeeSortValue = (user, columnId) => {
    const asDate = (value) => {
      if (!value) return 0;
      const t = new Date(value).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    switch (columnId) {
      case "firstName":
        return normalizeStr(user?.firstName ?? user?.first_name);
      case "lastName":
        return normalizeStr(user?.lastName ?? user?.last_name);
      case "name":
        return normalizeStr(
          `${user?.firstName ?? user?.first_name ?? ""} ${user?.lastName ?? user?.last_name ?? ""}`.trim()
        );
      case "companyEmail":
        return normalizeStr(user?.companyEmail ?? user?.company_email);
      case "designation":
        return normalizeStr(user?.designation ?? user?.designation_name);
      case "role":
        return normalizeStr(getUserRolesList(user).join(", "));
      case "employeeId":
        return normalizeStr(user?.employeeId ?? user?.employee_id);
      case "email":
        return normalizeStr(user?.email);
      case "aadhaar":
        return normalizeStr(user?.aadhaar);
      case "pan":
        return normalizeStr(user?.pan);
      case "department":
        return normalizeStr(user?.department);
      case "phone":
        return normalizeStr(user?.phoneNumber ?? user?.phone_number);
      case "band":
        return normalizeStr(user?.band);
      case "status":
        return normalizeStr(user?.status);
      case "dateOfJoining":
        return asDate(user?.dateOfJoining ?? user?.date_of_joining ?? user?.doj);
      case "dateOfLeaving":
        return asDate(user?.dateOfLeaving ?? user?.date_of_leaving);
      case "createdBy":
        return normalizeStr(user?.createdByName ?? user?.created_by_name);
      case "createdOn":
        return asDate(user?.createdOn ?? user?.created_on);
      case "updatedBy":
        return normalizeStr(user?.updatedByName ?? user?.updated_by_name);
      case "updatedOn":
        return asDate(user?.updatedOn ?? user?.updated_on);
      default:
        return "";
    }
  };

  const sortedUsers = useMemo(() => {
    if (!sortColumn) return freeSearchFilteredUsers;
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...freeSearchFilteredUsers].sort((a, b) => {
      const va = getEmployeeSortValue(a, sortColumn);
      const vb = getEmployeeSortValue(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a?.id ?? a?.user_id ?? 0);
      const idb = Number(b?.id ?? b?.user_id ?? 0);
      return (ida - idb) * mult;
    });
  }, [freeSearchFilteredUsers, sortColumn, sortDirection]);

  useEffect(() => {
    // When filters change, always reset to first page.
    setCurrentPage(1);
  }, [nameFilter, designationFilter, roleFilter, statusFilter]);

  // Close searchable dropdowns on outside click
  useEffect(() => {
    const handleDocumentMouseDown = (e) => {
      const target = e?.target;
      if (designationDropdownOpen && designationDropdownRef.current && !designationDropdownRef.current.contains(target)) {
        setDesignationDropdownOpen(false);
      }
      if (roleDropdownOpen && roleDropdownRef.current && !roleDropdownRef.current.contains(target)) {
        setRoleDropdownOpen(false);
      }
      if (statusDropdownOpen && statusDropdownRef.current && !statusDropdownRef.current.contains(target)) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
  }, [designationDropdownOpen, roleDropdownOpen, statusDropdownOpen]);

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentUsers = sortedUsers.slice(startIndex, endIndex);

  // Save current page to localStorage whenever it changes
  useEffect(() => {
    if (currentPage > 0) {
      localStorage.setItem('usersCurrentPage', currentPage.toString());
    }
  }, [currentPage]);

  // Reset to page 1 if current page exceeds total pages (e.g., after filtering)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(USERS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const handleView = (userId) => {
    if (onViewUser) {
      onViewUser(userId);
    }
  };

  const handleEdit = (userId) => {
    if (onEditUser) {
      onEditUser(userId);
    }
  };

  const handleDelete = (userId) => {
    if (onDeleteUser) {
      onDeleteUser(userId);
    }
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return "—";
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return dateVal;
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return dateVal;
    }
  };

  return (
    <div className="users-container">
      <div className="main-card">
        <div className="page-header">
          <div className={`header-actions users-filters-header co-filters-collapsible${filtersOpen || !isCompactViewport ? " is-open" : ""}`}>
            {isCompactViewport ? (
              <div className="co-filter-search-bar">
                <input
                  type="text"
                  className="co-filter-search-bar__input"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Search name..."
                  aria-label="Search name"
                />
                <button
                  type="button"
                  className={`co-filter-search-bar__icon${filtersOpen ? " is-active" : ""}`}
                  onClick={() => setFiltersOpen((o) => !o)}
                  aria-expanded={filtersOpen}
                  aria-label={filtersOpen ? "Hide Filters" : "Show Filters"}
                  title={filtersOpen ? "Hide Filters" : "Show Filters"}
                >
                  <FaSlidersH aria-hidden />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="co-filters-toggle"
                onClick={() => setFiltersOpen((o) => !o)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? "Hide Filters" : "Show Filters"}
                title={filtersOpen ? "Hide Filters" : "Show Filters"}
              >
                <FaSlidersH aria-hidden />
                <span className="co-filters-toggle__label">{filtersOpen ? "Hide Filters" : "Show Filters"}</span>
              </button>
            )}
            <div className="co-filters-body">
            <div className="users-filters-row">
              {!isCompactViewport ? (
                <input
                  className="users-filter-input"
                  type="text"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Search name..."
                  title="Filter by employee name"
                />
              ) : null}
              <div className="domain-filter-dropdown" ref={designationDropdownRef} style={{ minWidth: 180 }}>
                <div
                  className="search-filter-input search-filter-select domain-filter-trigger"
                  style={{
                    height: 38,
                    borderRadius: 6,
                    border: "1px solid #D6E0EA",
                    padding: "0 12px",
                    fontSize: 14,
                    background: "#fff",
                    minWidth: 180,
                    opacity: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setDesignationDropdownOpen((o) => !o);
                    setRoleDropdownOpen(false);
                    setStatusDropdownOpen(false);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDesignationDropdownOpen((o) => !o);
                      setRoleDropdownOpen(false);
                      setStatusDropdownOpen(false);
                    }
                  }}
                >
                  <span>{designationFilter || "Designation"}</span>
                  <FaChevronDown className={`domain-filter-chevron ${designationDropdownOpen ? "open" : ""}`} />
                </div>
                {designationDropdownOpen && (
                  <div className="domain-filter-panel">
                    <input
                      type="text"
                      className="domain-filter-search"
                      placeholder="Search designation..."
                      value={designationSearchQuery}
                      onChange={(e) => setDesignationSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                    <div className="domain-filter-options">
                      <div
                        className={`domain-filter-option ${!designationFilter ? "selected" : ""}`}
                        onClick={() => {
                          setDesignationFilter("");
                          setDesignationSearchQuery("");
                          setDesignationDropdownOpen(false);
                        }}
                      >
                        Designation
                      </div>
                      {designationOptions
                        .filter((d) => !designationSearchQuery.trim() || String(d).toLowerCase().includes(designationSearchQuery.trim().toLowerCase()))
                        .map((d) => (
                          <div
                            key={d}
                            className={`domain-filter-option ${String(designationFilter) === String(d) ? "selected" : ""}`}
                            onClick={() => {
                              setDesignationFilter(String(d));
                              setDesignationSearchQuery("");
                              setDesignationDropdownOpen(false);
                            }}
                          >
                            {d}
                          </div>
                        ))}
                      {designationOptions.filter((d) => !designationSearchQuery.trim() || String(d).toLowerCase().includes(designationSearchQuery.trim().toLowerCase())).length === 0 && (
                        <div className="domain-filter-no-results">No designations found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="domain-filter-dropdown" ref={roleDropdownRef} style={{ minWidth: 180 }}>
                <div
                  className="search-filter-input search-filter-select domain-filter-trigger"
                  style={{
                    height: 38,
                    borderRadius: 6,
                    border: "1px solid #D6E0EA",
                    padding: "0 12px",
                    fontSize: 14,
                    background: "#fff",
                    minWidth: 180,
                    opacity: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setRoleDropdownOpen((o) => !o);
                    setDesignationDropdownOpen(false);
                    setStatusDropdownOpen(false);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setRoleDropdownOpen((o) => !o);
                      setDesignationDropdownOpen(false);
                      setStatusDropdownOpen(false);
                    }
                  }}
                >
                  <span>{roleFilter || "Role"}</span>
                  <FaChevronDown className={`domain-filter-chevron ${roleDropdownOpen ? "open" : ""}`} />
                </div>
                {roleDropdownOpen && (
                  <div className="domain-filter-panel">
                    <input
                      type="text"
                      className="domain-filter-search"
                      placeholder="Search role..."
                      value={roleSearchQuery}
                      onChange={(e) => setRoleSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                    <div className="domain-filter-options">
                      <div
                        className={`domain-filter-option ${!roleFilter ? "selected" : ""}`}
                        onClick={() => {
                          setRoleFilter("");
                          setRoleSearchQuery("");
                          setRoleDropdownOpen(false);
                        }}
                      >
                        Role
                      </div>
                      {roleOptions
                        .filter((r) => !roleSearchQuery.trim() || String(r).toLowerCase().includes(roleSearchQuery.trim().toLowerCase()))
                        .map((r) => (
                          <div
                            key={r}
                            className={`domain-filter-option ${String(roleFilter) === String(r) ? "selected" : ""}`}
                            onClick={() => {
                              setRoleFilter(String(r));
                              setRoleSearchQuery("");
                              setRoleDropdownOpen(false);
                            }}
                          >
                            {r}
                          </div>
                        ))}
                      {roleOptions.filter((r) => !roleSearchQuery.trim() || String(r).toLowerCase().includes(roleSearchQuery.trim().toLowerCase())).length === 0 && (
                        <div className="domain-filter-no-results">No roles found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="domain-filter-dropdown" ref={statusDropdownRef} style={{ minWidth: 180 }}>
                <div
                  className="search-filter-input search-filter-select domain-filter-trigger"
                  style={{
                    height: 38,
                    borderRadius: 6,
                    border: "1px solid #D6E0EA",
                    padding: "0 12px",
                    fontSize: 14,
                    background: "#fff",
                    minWidth: 180,
                    opacity: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setStatusDropdownOpen((o) => !o);
                    setDesignationDropdownOpen(false);
                    setRoleDropdownOpen(false);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setStatusDropdownOpen((o) => !o);
                      setDesignationDropdownOpen(false);
                      setRoleDropdownOpen(false);
                    }
                  }}
                >
                  <span>{statusFilter || "Status"}</span>
                  <FaChevronDown className={`domain-filter-chevron ${statusDropdownOpen ? "open" : ""}`} />
                </div>
                {statusDropdownOpen && (
                  <div className="domain-filter-panel">
                    <input
                      type="text"
                      className="domain-filter-search"
                      placeholder="Search status..."
                      value={statusSearchQuery}
                      onChange={(e) => setStatusSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                    <div className="domain-filter-options">
                      <div
                        className={`domain-filter-option ${!statusFilter ? "selected" : ""}`}
                        onClick={() => {
                          setStatusFilter("");
                          setStatusSearchQuery("");
                          setStatusDropdownOpen(false);
                        }}
                      >
                        Status
                      </div>
                      {statusOptions
                        .filter((s) => !statusSearchQuery.trim() || String(s).toLowerCase().includes(statusSearchQuery.trim().toLowerCase()))
                        .map((s) => (
                          <div
                            key={s}
                            className={`domain-filter-option ${String(statusFilter) === String(s) ? "selected" : ""}`}
                            onClick={() => {
                              setStatusFilter(String(s));
                              setStatusSearchQuery("");
                              setStatusDropdownOpen(false);
                            }}
                          >
                            {s}
                          </div>
                        ))}
                      {statusOptions.filter((s) => !statusSearchQuery.trim() || String(s).toLowerCase().includes(statusSearchQuery.trim().toLowerCase())).length === 0 && (
                        <div className="domain-filter-no-results">No statuses found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn-clear"
                onClick={() => {
                  setNameFilter("");
                  setDesignationFilter("");
                  setRoleFilter("");
                  setStatusFilter("");
                  setFreeSearch("");
                  setCurrentPage(1);
                  setDesignationDropdownOpen(false);
                  setRoleDropdownOpen(false);
                  setStatusDropdownOpen(false);
                  setDesignationSearchQuery("");
                  setRoleSearchQuery("");
                  setStatusSearchQuery("");
                }}
              >
                Clear
              </button>
            </div>
            </div>
            {canAdd && (
              <button className="btn-add-user" onClick={onAddUserClick}>
                New Employee
              </button>
            )}
          </div>
        </div>

        <div className="search-row-2 co-results-sticky">
          <div className="free-search-bar" style={{ marginLeft: "auto", maxWidth: 280 }}>
            <input
              type="text"
              className="free-search-input"
              placeholder="Search..."
              value={freeSearch}
              onChange={(e) => {
                setFreeSearch(e.target.value);
                setCurrentPage(1);
              }}
              title="Search across employee table columns"
            />
          </div>
        </div>

        <div className="table-wrapper co-desktop-table">
          <div className="table-container">
            <table className="data-table users-table-frozen">
              <thead>
                <tr>
                  <th className="col-sticky col-0 projects-th-sortable" style={getColumnStyle(0)}>
                    <div className="projects-th-sort-row">
                      <SupportSortableHeader columnId="employeeId" label="Emp ID" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(0, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(1)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center">
                      <SupportSortableHeader columnId="name" label="Name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(1, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(2)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center">
                      <SupportSortableHeader columnId="companyEmail" label="Company Email" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(2, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(3)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center">
                      <SupportSortableHeader columnId="designation" label="Designation" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(3, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(4)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center">
                      <SupportSortableHeader columnId="role" label="Role" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(4, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(5)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center">
                      <SupportSortableHeader columnId="email" label="Email" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(5, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(6)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center">
                      <SupportSortableHeader columnId="aadhaar" label="Aadhaar" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(6, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(7)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center">
                      <SupportSortableHeader columnId="pan" label="PAN" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(7, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(8)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center">
                      <SupportSortableHeader columnId="department" label="Department" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(8, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(9)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center">
                      <SupportSortableHeader columnId="phone" label="Phone" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(9, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(10)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center">
                      <SupportSortableHeader columnId="band" label="Band" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(10, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(11)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center">
                      <SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(11, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(12)}>
                    <div className="projects-th-sort-row">
                      <SupportSortableHeader columnId="dateOfJoining" label="Date of Joining" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(12, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(13)}>
                    <div className="projects-th-sort-row">
                      <SupportSortableHeader columnId="dateOfLeaving" label="Date of Leaving" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(13, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(14)}>
                    <div className="projects-th-sort-row">
                      <SupportSortableHeader columnId="createdBy" label="Created By" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(14, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(15)}>
                    <div className="projects-th-sort-row">
                      <SupportSortableHeader columnId="createdOn" label="Created On" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(15, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(16)}>
                    <div className="projects-th-sort-row">
                      <SupportSortableHeader columnId="updatedBy" label="Updated By" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(16, e);
                      }}
                    />
                  </th>
                  <th className="projects-th-sortable" style={getColumnStyle(17)}>
                    <div className="projects-th-sort-row">
                      <SupportSortableHeader columnId="updatedOn" label="Updated On" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <span
                      className="column-resizer"
                      aria-hidden
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleResizeStart(17, e);
                      }}
                    />
                  </th>
                  {/* Keep Action column non-resizable to avoid layout issues with sticky buttons */}
                  <th className="col-sticky col-action" style={getColumnStyle(18)}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="19" style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                      Loading users...
                    </td>
                  </tr>
                ) : sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="19" style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                      No employees found. Click "New Employee" to add one.
                    </td>
                  </tr>
                ) : (
                  currentUsers.map((user) => {
                    const userId = user.id || user.user_id;
                    const fullName = `${user.firstName || user.first_name || ""} ${user.lastName || user.last_name || ""}`.trim() || "—";
                    const rolesDisplay = Array.isArray(user.roles) && user.roles.length > 0
                      ? user.roles
                          .map((r) => r.roleName || r.role_name || r.name || r.role || "")
                          .filter((name) => (name || "").trim() !== "")
                          .join(", ")
                      : user.role || user.role_name || "—";
                    return (
                      <tr key={userId || Math.random()}>
                        <td className="col-sticky col-0">{user.employeeId || user.employee_id || "—"}</td>
                        <td>
                          <span
                            className="table-name-link"
                            onClick={() => handleView(userId)}
                            onKeyDown={(e) => { 
                              if (e.key === "Enter" || e.key === " ") { 
                                e.preventDefault(); 
                                handleView(userId); 
                              } 
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {fullName}
                          </span>
                        </td>
                        <td>{user.companyEmail || user.company_email || "—"}</td>
                        <td>{user.designation || user.designation_name || "—"}</td>
                        <td>{rolesDisplay}</td>
                        <td>{user.email || "—"}</td>
                        <td>{user.aadhaar || "—"}</td>
                        <td>{user.pan || "—"}</td>
                        <td>{user.department || "—"}</td>
                        <td>
                          {(() => {
                            const phone = String(user.phoneNumber || user.phone_number || "").trim();
                            if (!phone) return "—";
                            return formatPhoneSpaced(phoneCountryCode(user), phone);
                          })()}
                        </td>
                        <td>{user.band || "—"}</td>
                        <td>{user.status || "—"}</td>
                        <td>{formatDate(user.dateOfJoining || user.date_of_joining || user.doj)}</td>
                        <td>{formatDate(user.dateOfLeaving || user.date_of_leaving)}</td>
                        <td>{user.createdByName || user.created_by_name || "—"}</td>
                        <td>{formatDate(user.createdOn || user.created_on)}</td>
                        <td>{user.updatedByName || user.updated_by_name || "—"}</td>
                        <td>{formatDate(user.updatedOn || user.updated_on)}</td>
                        <td className="col-sticky col-action">
                          <div className="action-buttons">
                            {renderPhoneAction(user)}
                            {renderCompanyEmailAction(user)}
                            {canEdit && (
                              <button
                                className="btn-edit"
                                onClick={() => handleEdit(userId)}
                                title="Edit User"
                              >
                                <FaEdit />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                className="btn-delete"
                                onClick={() => handleDelete(userId)}
                                title="Deactivate User"
                              >
                                <FaTrashAlt />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ResultsMobileList loading={loading} loadingLabel="Loading users…" emptyLabel='No employees found. Click "New Employee" to add one.'>
          {!loading &&
            currentUsers.map((user) => {
              const userId = user.id || user.user_id;
              const fullName =
                `${user.firstName || user.first_name || ""} ${user.lastName || user.last_name || ""}`.trim() || "—";
              const rolesDisplay =
                Array.isArray(user.roles) && user.roles.length > 0
                  ? user.roles
                      .map((r) => r.roleName || r.role_name || r.name || r.role || "")
                      .filter((name) => (name || "").trim() !== "")
                      .join(", ")
                  : user.role || user.role_name || null;
              const personalPhone = String(user.phoneNumber || user.phone_number || "").trim();
              const personalCountryCode = personalPhone.startsWith("+") ? "" : phoneCountryCode(user);
              const personalPhoneLabel = formatPhoneSpaced(personalCountryCode, personalPhone);
              const personalEmail = String(user.email || "").trim();
              const hasCompanyEmail = Boolean(String(user.companyEmail || user.company_email || "").trim());
              return (
                <ResultsMobileCard
                  key={userId || fullName}
                  title={fullName}
                  meta={[
                    {
                      label:
                        user.employeeId || user.employee_id
                          ? `#${user.employeeId || user.employee_id}`
                          : user.status,
                      tone: String(user.status || "").toLowerCase() === "active" ? "success" : "danger",
                      status: true,
                    },
                  ]}
                  fields={[
                    { label: "Designation", value: user.designation || user.designation_name },
                    { label: "Role", value: rolesDisplay },
                    { label: "Department", value: user.department },
                  ]}
                  onOpen={() => handleView(userId)}
                  actions={
                    <>
                      {hasCompanyEmail ? renderCompanyEmailAction(user, { tooltip: true }) : null}
                      {canEdit ? (
                        <button type="button" className="btn-edit" title="Edit User" onClick={() => handleEdit(userId)}>
                          <FaEdit />
                        </button>
                      ) : null}
                      {canDelete ? (
                        <button type="button" className="btn-delete" title="Deactivate User" onClick={() => handleDelete(userId)}>
                          <FaTrashAlt />
                        </button>
                      ) : null}
                    </>
                  }
                >
                  {personalPhone || personalEmail ? (
                    <ul className="co-tcard__contact">
                      {personalPhone ? (
                        <li>
                          <FaPhone aria-hidden="true" style={{ transform: "scaleX(-1)" }} />
                          <a
                            className="users-contact-link"
                            href={`tel:${`${personalCountryCode}${personalPhone}`.replace(/[^\d+]/g, "")}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {personalPhoneLabel}
                          </a>
                        </li>
                      ) : null}
                      {personalEmail ? (
                        <li>
                          <FaEnvelope aria-hidden="true" />
                          <a
                            className="users-contact-link"
                            href={`mailto:${personalEmail}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {personalEmail}
                          </a>
                        </li>
                      ) : null}
                    </ul>
                  ) : null}
                </ResultsMobileCard>
              );
            })}
        </ResultsMobileList>

        {!loading && sortedUsers.length > 0 ? (
          <TablePagination
            currentPage={currentPage}
            totalItems={sortedUsers.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            itemLabel="employees"
            rowsPerPageSelectId="employees-rows-per-page"
          />
        ) : null}
      </div>
    </div>
  );
}

export default Users;
